import Layout from "./components/layout/Layout";
import {Route, Routes} from "react-router-dom";
import {Navigate} from "react-router";
import {ApplyContexts, ContextMenuProvider, FormikFormFiller, Theme} from "fearless-common-ui";
import { websiteTheme, websitePublicTheme } from "fearless-common-ui/dist/themes/themes";
import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import { routes } from "./utils";
import Dashboard from "./pages/Dashboard";
import {AuthProvider} from "./components/hooks/useAuth";
import Logout from "./pages/Logout";
import {QueryClient, QueryClientProvider} from 'react-query'
import PersonalInformationForm from "./pages/user/PersonalInformationForm";
import SignUp from "./pages/SignUp";
import SignUpSuccess from "./pages/SignUpSuccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import DownloadPhoto51 from "./pages/DownloadPhoto51";
import Photo51 from "./pages/Photo51";
import EmailVerified from "./pages/EmailVerified";
import CompleteAccount from "./pages/CompleteAccount";
import CompleteAccountSuccess from "./pages/CompleteAccountSuccess";
import AboutUs from "./pages/AboutUs";
import LoginLanding from "./pages/user/LoginLanding";
import Plans from "./pages/Plans";
import TwoFactorAuthentication from "./pages/2fa/TwoFactorAuthentication";
import TwoFactorAuthenticationSuccess from "./pages/2fa/TwoFactorAuthenticationSuccess";
import ProtectedRoute from "./pages/guards/ProtectedRoute";
import TwoFactorChallenge from "./pages/TwoFactorChallenge";
import LoggedInUserGroups from "./pages/LoggedInUserGroups";
import {useEffect} from "react";
import {IS_DEV, isDev, isInternal, isInUserEnvironment, isProd} from "./utils/env";
import FaqPage from "./pages/FaqPage";
import {addScript} from "./utils/htmlHead";
import {GOOGLE_TAG_ID} from "./tracking/google-analytics";
import {adminRoutes, externalLinks} from "./utils/routes";
import RedirectPage from "./pages/RedirectPage";
import InvalidLink from "./pages/errors/InvalidLink";
import JoinGroupLink from "./pages/shareable-links/joining-group-process/JoinGroupLink";
import OpenPhoto51 from "./pages/OpenPhoto51";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUser from "./pages/admin/AdminUser";
import AdminGroups from "./pages/admin/AdminGroups";
import AdminGroup from "./pages/admin/AdminGroup";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminCourse from "./pages/admin/AdminCourse";
import AdminSubscriptionRules from "./pages/admin/subscription-rules/SubscriptionRules";
import AdminSubscriptionRule from "./pages/admin/subscription-rules/SubscriptionRule";
import ConfirmationModalProvider from "./components/contexts/ConfirmationModalContext";
import ShareRequestAccessLink from "./pages/shareable-links/ShareRequestAccessLink";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminSubscription from "./pages/admin/AdminSubscription";
import AdminInsights from "./pages/admin/AdminInsights";
import AdminInstitutes from "./pages/admin/AdminInstitutes";
import AdminInstitute from "./pages/admin/AdminInstitute";
import PersonalInformationView from "./pages/user/PersonalInformationView";
import SecurityPage from "./pages/user/SecurityPage";
import GetIndividualSubscription from "./pages/GetIndividualSubscription";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminStripePrices from "./pages/admin/AdminStripePrices";
import { Toaster } from "react-hot-toast";
import AdminUserAdd from "./pages/admin/subscription-rules/AdminUserAdd";
import AdminCommunicationMocks from "./components/admin/AdminCommunicationMocks";
import AdminSignUpCodes from "./pages/admin/AdminSignUpCodes";
import AdminSignUpCode from "./pages/admin/AdminSignUpCode";
import AdminFiles from "./pages/admin/file/AdminFiles";
import AdminLoggedUIEvents from "./pages/admin/AdminLoggedUIEvents";
import JoinCourseLink from "./pages/shareable-links/joining-course-process/JoinCourseLink";
import AdminNotifications from "./pages/admin/notifications/AdminNotifications";
import AdminSendNotificationsForm from "./pages/admin/notifications/AdminSendNotificationsForm";
import ResourcesOverview from "./components/user-resources/ResourcesOverview";
import ResourcePage from "./components/user-resources/ResourcePage";
import AdminFile from "./pages/admin/file/AdminFile";
import AdminReagent from "./pages/admin/reagents/AdminReagent";
import SparkELN from "./pages/products/SparkELN";
import {PasswordConfirmationProvider} from "./components/PasswordConfirmationButton";

// Create a client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            retry: false,
            staleTime: 5*60*1000,
        },
    },
})

const contexts = [ConfirmationModalProvider, AuthProvider, ContextMenuProvider]
  .concat(isDev() || isInternal() ? [FormikFormFiller.Provider] : [])
  .concat([PasswordConfirmationProvider]);

export default function App() {
    useEffect(() => {
        if (isInternal() || isProd()) {
            addScript({ src: `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`, async: true })
            addScript({
                html: "window.dataLayer = window.dataLayer || [];\n" +
                    "function gtag(){dataLayer.push(arguments);}\n" +
                    "gtag('js', new Date());\n" +
                    `gtag('config', '${GOOGLE_TAG_ID}');`
            })
        }

        if (IS_DEV || isInternal()) {
            // DO not track https://internal.fearlessleague.com in hotjar.
            // No reason to have heatmaps, etc. for internal...
        } else {
            // Hotjar Tracking Code for https://fearlessleague.com
            addScript({
                location: 'head',
                html:
                    "(function(h,o,t,j,a,r){\n" +
                    "    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};\n" +
                    "    h._hjSettings={hjid:3280770,hjsv:6};\n" +
                    "    a=o.getElementsByTagName('head')[0];\n" +
                    "    r=o.createElement('script');r.async=1;\n" +
                    "    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;\n" +
                    "    a.appendChild(r);\n" +
                    "})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');\n"
            });
        }
    }, [])

    return (
        <Theme theme={isInUserEnvironment() ? websiteTheme : websitePublicTheme}>
            <Toaster />
            <ApplyContexts contexts={contexts}>
                <QueryClientProvider client={queryClient}>
                    <Layout>
                        <Routes>
                            <Route path={adminRoutes.subscription(':subscriptionId')} element={<ProtectedRoute><AdminSubscription /></ProtectedRoute>} />
                            <Route path={adminRoutes.subscriptions} element={<ProtectedRoute><AdminSubscriptions /></ProtectedRoute>} />
                            <Route path={adminRoutes.loggedUiEvents} element={<ProtectedRoute><AdminLoggedUIEvents /></ProtectedRoute>} />
                            <Route path={adminRoutes.communicationMocks} element={<ProtectedRoute><AdminCommunicationMocks /></ProtectedRoute>} />
                            <Route path={adminRoutes.insights} element={<ProtectedRoute><AdminInsights /></ProtectedRoute>} />
                            <Route path={adminRoutes.reagent(':reagentId', ':tab')} element={<ProtectedRoute><AdminReagent /></ProtectedRoute>} />
                            <Route path={adminRoutes.reagent(':reagentId', '')} element={<ProtectedRoute><AdminReagent /></ProtectedRoute>} />
                            <Route path={adminRoutes.file(':fileId', ':tab')} element={<ProtectedRoute><AdminFile /></ProtectedRoute>} />
                            <Route path={adminRoutes.file(':fileId', '')} element={<ProtectedRoute><AdminFile /></ProtectedRoute>} />
                            <Route path={adminRoutes.files} element={<ProtectedRoute><AdminFiles /></ProtectedRoute>} />
                            <Route path={adminRoutes.products} element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
                            <Route path={adminRoutes.prices} element={<ProtectedRoute><AdminStripePrices /></ProtectedRoute>} />
                            <Route path={adminRoutes.users} element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
                            <Route path={adminRoutes.notifications} element={<ProtectedRoute><AdminNotifications /></ProtectedRoute>} />
                            <Route path={adminRoutes.sendNotification} element={<ProtectedRoute><AdminSendNotificationsForm /></ProtectedRoute>} />
                            <Route
                              path={adminRoutes.user(':userId', ':tab')}
                              element={<ProtectedRoute><AdminUser /></ProtectedRoute>}
                            />
                            <Route
                              path={adminRoutes.user(':userId', '')}
                              element={<ProtectedRoute><AdminUser /></ProtectedRoute>}
                            />
                            <Route path={adminRoutes.addUser} element={<ProtectedRoute><AdminUserAdd /></ProtectedRoute>} />
                            <Route path={adminRoutes.groups} element={<ProtectedRoute><AdminGroups /></ProtectedRoute>} />
                            <Route path={adminRoutes.group(':groupId')} element={<ProtectedRoute><AdminGroup /></ProtectedRoute>} />
                            <Route path={adminRoutes.courses} element={<ProtectedRoute><AdminCourses /></ProtectedRoute>} />
                            <Route path={adminRoutes.course(':courseId', ':tab')} element={<ProtectedRoute><AdminCourse /></ProtectedRoute>} />
                            <Route path={adminRoutes.course(':courseId')} element={<ProtectedRoute><AdminCourse /></ProtectedRoute>} />
                            <Route path={adminRoutes.subscriptionRules} element={<ProtectedRoute><AdminSubscriptionRules /></ProtectedRoute>} />
                            <Route path={adminRoutes.institute(':instituteId')} element={<ProtectedRoute><AdminInstitute /></ProtectedRoute>} />
                            <Route path={adminRoutes.institutes} element={<ProtectedRoute><AdminInstitutes /></ProtectedRoute>} />
                            <Route path={adminRoutes.signUpCode(':signUpCodeId')} element={<ProtectedRoute><AdminSignUpCode /></ProtectedRoute>} />
                            <Route path={adminRoutes.signUpCodes} element={<ProtectedRoute><AdminSignUpCodes /></ProtectedRoute>} />
                            <Route path={adminRoutes.subscriptionRule(':ruleId')} element={<ProtectedRoute><AdminSubscriptionRule /></ProtectedRoute>} />

                            <Route path={routes.accountEdit} element={<ProtectedRoute><PersonalInformationForm /></ProtectedRoute>}/>
                            <Route path={routes.personalInformation} element={<ProtectedRoute><PersonalInformationView /></ProtectedRoute>}/>
                            <Route path={routes.security} element={<ProtectedRoute><SecurityPage /></ProtectedRoute>}/>
                            <Route path={routes.getIndividualSubscription} element={<ProtectedRoute><GetIndividualSubscription /></ProtectedRoute>}/>
                            <Route path={routes.subscriptions()} element={<ProtectedRoute><Dashboard /></ProtectedRoute>}/>
                            <Route path={routes.subscriptions(':tabId')} element={<ProtectedRoute><Dashboard /></ProtectedRoute>}/>
                            <Route path={routes.twoFactorAuthentication} element={<ProtectedRoute><TwoFactorAuthentication /></ProtectedRoute>}/>
                            <Route path={routes.twoFactorAuthenticationSuccess} element={<ProtectedRoute><TwoFactorAuthenticationSuccess /></ProtectedRoute>}/>
                            <Route path={routes.twoFactorChallenge} element={<TwoFactorChallenge />}/>
                            <Route path={routes.login} element={<LoginPage />} />
                            <Route path={routes.openPhoto51} element={<OpenPhoto51 />} />
                            <Route path={routes.groupsJoinLink(':groupId', ':hash')} element={<JoinGroupLink />} />
                            <Route path={routes.coursesJoinLink(':courseId', ':hash')} element={<JoinCourseLink />} />
                            <Route path={routes.completeAccount(':userId', ':hash')} element={<CompleteAccount />} />
                            <Route path={routes.shareRequestAccessLink(':shareRequestIds', ':hash')} element={<ShareRequestAccessLink />} />
                            <Route path={routes.emailVerified} element={<EmailVerified />} />
                            <Route path={routes.download} element={<DownloadPhoto51 />} />
                            <Route path={routes.photo51} element={<Photo51 />} />
                            <Route path={routes.spark} element={<SparkELN />} />
                            <Route path={routes.plans} element={<Plans />} />
                            <Route path={routes.signUpSuccess} element={<SignUpSuccess />} />
                            <Route path={routes.completeAccountSuccess} element={<CompleteAccountSuccess />} />
                            <Route path={routes.signUp} element={<SignUp />} />
                            <Route path={routes.cookiePolicy} element={<RedirectPage to={externalLinks.cookiePolicy} />} />
                            <Route path={routes.privacy} element={<RedirectPage to={externalLinks.privacyPolicy} />} />
                            <Route path={routes.termsOfService} element={<RedirectPage to={externalLinks.termsOfService} />} />
                            <Route path={routes.logout} element={<Logout />} />
                            <Route path={routes.aboutUs} element={<AboutUs />} />
                            <Route path={routes.resourceView(':id')} element={<ResourcePage />} />
                            <Route path={routes.resources} element={<ResourcesOverview />} />
                            <Route path={routes.landing} element={<ProtectedRoute><LoginLanding /></ProtectedRoute>} />
                            <Route path={routes.forgotPassword} element={<ForgotPassword />} />
                            <Route path={routes.resetPassword} element={<ResetPassword />} />
                            <Route path={routes.faq} element={<FaqPage />} />
                            {[routes.groups, routes.group(':groupId')].map((p) => (
                                <Route
                                    key={p}
                                    path={p}
                                    element={<ProtectedRoute><LoggedInUserGroups /></ProtectedRoute>}
                                />
                            ))}
                            <Route
                                path="/group/groups"
                                element={<Navigate to={routes.groups} replace />}
                            />
                            {/* Old website url whereto Photo51 cloud redirected */}
                            <Route path={routes.dashboard} Component={() => <ProtectedRoute><RedirectPage to={routes.landing} /></ProtectedRoute>} />
                            <Route path="/" element={<Home/>} />
                            <Route path={routes.invalidLink} element={<InvalidLink />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Layout>
                </QueryClientProvider>
            </ApplyContexts>
        </Theme>
    );
}
